import { Injectable, Optional, SkipSelf } from '@angular/core';
import { SchedulerCommandStack } from '@shared/models/command-history/scheduler-command-history/scheduler-command.models';
import { BehaviorSubject, EMPTY, filter, from, map, Observable, Subscriber, switchMap, take } from 'rxjs';
import { openDB, IDBPDatabase } from 'idb';

const DB_NAME: string = 'SchedulerCommandDB';
const STORE_NAME: string = 'SessionCommandHistory';
const DB_VERSION: number = 1;

@Injectable({ providedIn: 'root' })
export class SchedulerCommandHistoryStorage {
  #database!: IDBPDatabase;

  #dbInitialized$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(@Optional() @SkipSelf() existingStorage: SchedulerCommandHistoryStorage) {
    if (existingStorage) {
      throw new Error('SchedulerDbApi is already loaded. It should only be imported in the AppModule.');
    }

    this.initDB().subscribe((database: IDBPDatabase<unknown>) => {
      this.#database = database;

      this.#dbInitialized$.next(true);
    });
  }

  getStack(sessionId: string): Observable<SchedulerCommandStack | undefined> {
    return this.runWhenDbReady(() => from(this.#database.get(STORE_NAME, sessionId)));
  }

  saveStack(sessionId: string, data: SchedulerCommandStack): Observable<void> {
    return this.runWhenDbReady(() => from(this.#database.put(STORE_NAME, data, sessionId)).pipe(map(() => undefined)));
  }

  deleteStack(sessionId: string): Observable<void> {
    return this.runWhenDbReady(() => from(this.#database.delete(STORE_NAME, sessionId)));
  }

  clearAll(): Observable<void> {
    return this.runWhenDbReady(() => from(this.#database.clear(STORE_NAME)));
  }

  private runWhenDbReady<T>(operation: () => Observable<T>): Observable<T> {
    return this.#dbInitialized$.pipe(filter(Boolean), take(1)).pipe(switchMap(() => operation()));
  }

  private initDB(): Observable<IDBPDatabase<unknown>> {
    if (this.#database) return EMPTY;

    return new Observable<IDBPDatabase<unknown>>((subscriber: Subscriber<unknown>) => {
      openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        },
      }).then((database: IDBPDatabase<unknown>) => {
        subscriber.next(database);

        subscriber.complete();
      });
    });
  }
}
